import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import DragIndicator from '@mui/icons-material/DragIndicator'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget, parseNumberParam } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchPeriod: 0,
    searchStatus: 0,
}

const SEARCH_SELECT_PARAMS = {
    searchPeriod: {
        param: 'period',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPeriod,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

export default function AppCompanys({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 30
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [dragCompanyId, setDragCompanyId] = useState(null)
    const [dragOverCompanyId, setDragOverCompanyId] = useState(null)
    const [dragOverPosition, setDragOverPosition] = useState('after')
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const silentListRefreshRef = useRef(false)
    const canSort = role >= 4 && !searchQuery && Number(searchSelect.searchStatus) === 0 && Number(searchSelect.searchPeriod || 0) === 0
    
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/getList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '기업 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '기업 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    const clearDragState = () => {
        setDragCompanyId(null)
        setDragOverCompanyId(null)
        setDragOverPosition('after')
    }

    const handleDragStart = (companyId) => (e) => {
        if (!canSort || isSubmitting) {
            e.preventDefault()
            return
        }

        setDragCompanyId(companyId)
        setDragOverCompanyId(null)
        setDragOverPosition('after')
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(companyId))
    }

    const handleDragOver = (companyId) => (e) => {
        if (!canSort || isSubmitting) {
            return
        }

        const sourceCompanyId = Number(dragCompanyId || e.dataTransfer.getData('text/plain'))
        if (!sourceCompanyId || sourceCompanyId === Number(companyId)) {
            return
        }

        e.preventDefault()

        const rect = e.currentTarget.getBoundingClientRect()
        const position = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after'

        if (dragOverCompanyId !== companyId || dragOverPosition !== position) {
            setDragOverCompanyId(companyId)
            setDragOverPosition(position)
        }

        e.dataTransfer.dropEffect = 'move'
    }

    const handleDrop = (targetCompanyId) => async (e) => {
        e.preventDefault()

        if (!canSort || isSubmitting) {
            clearDragState()
            return
        }

        const sourceCompanyId = Number(dragCompanyId || e.dataTransfer.getData('text/plain'))
        if (!sourceCompanyId || sourceCompanyId === Number(targetCompanyId)) {
            clearDragState()
            return
        }

        const rect = e.currentTarget.getBoundingClientRect()
        const nextPosition = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after'

        setIsSubmitting(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/dragSortUpdate', {
                sourceCompanyId,
                targetCompanyId,
                position: nextPosition,
            })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
                await getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(err?.response?.data?.message || err.message)
        } finally {
            setIsSubmitting(false)
            clearDragState()
        }
    }

    const handleVisibleUpdate = async (companyId, isSignUpVisible) => {
        if (isSubmitting) {
            return
        }

        const nextVisible = Number(isSignUpVisible)
        const prevParams = params

        setParams(prev => prev.map(item =>
            item.companyId === companyId
                ? { ...item, isSignUpVisible: nextVisible }
                : item
        ))
        setIsSubmitting(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/visibleUpdate', { companyId, isSignUpVisible: nextVisible })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
            } else if (data.code === 403) {
                setParams(prevParams)
                history.push('/login')
            } else {
                setParams(prevParams)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            setParams(prevParams)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(err?.response?.data?.message || err.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (isSubmitting) {
            return
        }

        const { name, value } = e.target

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setSearchSelect(prev => ({
            ...prev,
            [name]: value
        }))
        setCurPage(1)
    }

    const resetFilters = () => {
        if (isSubmitting) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isSubmitting) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)
        || String(searchSelect.searchPeriod) !== String(DEFAULT_SEARCH_SELECT.searchPeriod)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="기업 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={isSubmitting}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 기업이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isSubmitting} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 기업이 없습니다."
                description="기업이 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>기업 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isSubmitting} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="도메인"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={isSubmitting}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isSubmitting}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="플랜상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                disabled={isSubmitting}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>플랜이용중</MenuItem>
                                <MenuItem value={2}>플랜만료</MenuItem>
                            </TextField>
                            <TextField
                                label="플랜기간"
                                select
                                name="searchPeriod"
                                value={searchSelect.searchPeriod}
                                onChange={updateSearchSelect}
                                disabled={isSubmitting}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>짧은순</MenuItem>
                                <MenuItem value={2}>오래남은순</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={role >= 4 && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isSubmitting}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appCompany',
                                    search: location.search,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect,
                                    curPage: curPage
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">순서변경</TableCell>
                                    <TableCell>정렬</TableCell>
                                    <TableCell>회원가입 노출</TableCell>
                                    <TableCell>이메일</TableCell>
                                    <TableCell>기업명</TableCell>
                                    <TableCell>도메인</TableCell>
                                    <TableCell>용량</TableCell>
                                    <TableCell>플랜명</TableCell>
                                    <TableCell>플랜이용기간</TableCell>
                                    <TableCell>플랜최초시작일</TableCell>
                                    {/* <TableCell>플랜남은일수</TableCell> */}
                                    {/* <TableCell>메모</TableCell> */}
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow
                                            key={row.companyId}
                                            onDragOver={handleDragOver(row.companyId)}
                                            onDrop={handleDrop(row.companyId)}
                                            sx={dragOverCompanyId === row.companyId ? {
                                                backgroundColor: 'action.hover',
                                                '& td': {
                                                    borderTop: dragOverPosition === 'before' ? '2px solid' : undefined,
                                                    borderBottom: dragOverPosition === 'after' ? '2px solid' : undefined,
                                                    borderColor: 'primary.main',
                                                }
                                            } : undefined}
                                        >
                                            <TableCell align="center">
                                                {canSort && (
                                                    <Box
                                                        draggable={!isSubmitting}
                                                        onDragStart={handleDragStart(row.companyId)}
                                                        onDragEnd={clearDragState}
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'text.secondary',
                                                            cursor: isSubmitting ? 'default' : 'grab',
                                                            opacity: dragCompanyId === row.companyId ? 0.35 : 1,
                                                        }}
                                                    >
                                                        <DragIndicator />
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>{row.sort}</TableCell>
                                            <TableCell>
                                                {role >= 4 ? (
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={Number(row.isSignUpVisible)}
                                                        onChange={(e) => handleVisibleUpdate(row.companyId, e.target.value)}
                                                        disabled={isSubmitting}
                                                        sx={{ minWidth: 100 }}
                                                    >
                                                        <MenuItem value={1}>노출</MenuItem>
                                                        <MenuItem value={0}>미노출</MenuItem>
                                                    </TextField>
                                                ) : (
                                                    Number(row.isSignUpVisible) === 1 ? '노출' : '미노출'
                                                )}
                                            </TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Box>{row.companyName}</Box>
                                                    {!!row.companyNameEn && (
                                                        <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                                                            {row.companyNameEn}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{Number(row.domainCount || 0)}</TableCell>
                                            <TableCell>{row.limitUsers}</TableCell>
                                            <TableCell>{row.planType === 1 ? '기간형' : '구독형'}</TableCell>
                                            <TableCell>{!row.planStartTime ? '' : row.planStartTime + ' ~ ' + row.planEndTime}</TableCell>
                                            <TableCell>{!row.planFirstTime ? '' : row.planFirstTime}</TableCell>
                                            {/* <TableCell>{!row.company_plan.length ? row.company_plan[0].dDay * -1 : ''}</TableCell> */}
                                            {/* <TableCell className="listTableLimitText">{row.memo}</TableCell> */}
                                            <TableCell align="right">
                                                <ListActionGroup disabled={isSubmitting}>
                                                    <EditIcon path={'appCompany'} id={row.companyId} search={location.search} searchText={searchQuery} searchSelect={searchSelect} curPage={curPage}/>
                                                    {/* {role === 5 && <DeleteAlertDialog handleDelete={handleDelete} id={row.companyId}/>} */}
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
